import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { CreativesApi } from '@/services/creatives/creativesApi'
import type { BidSpecification, CurrencyAmount } from '@/services/lineItems/lineItemsApi'
import {
  calculateNetCpm,
  collapseFormatFees,
  resolveFeeCurrency,
  type CpmBreakdown,
  type DisplayFee,
  type Fee,
} from '@/services/earnings/fees'

/**
 * Every fee charged against the line item's gross CPM, in the order the
 * breakdown lists them: the seller's own fees first, then the ones the platform
 * derives (tech, audience, supplementary), then the creative format fee.
 *
 * `formatFees` are the per-creative fees; they collapse into a single line, and
 * into a range when the creatives charge different amounts.
 */
export function cpmFeesOf(
  bid: BidSpecification | undefined,
  currency: string | undefined,
  formatFees: Fee[] = [],
  numCreatives = 0
): DisplayFee[] {
  const fees: DisplayFee[] = []
  const add = (fee: Fee | undefined, source: DisplayFee['source']) => {
    if (fee?.type) fees.push({ ...resolveFeeCurrency(fee, currency), source })
  }

  bid?.cpmFees?.forEach((fee) => add(fee, 'CPM'))
  add(bid?.cpmTechFee, 'TECH')
  bid?.audienceFees?.forEach((fee) => add(fee, 'AUDIENCE'))
  bid?.supplementaryFees?.forEach((fee) => add(fee, 'SUPPLEMENTARY'))

  const formatFee = collapseFormatFees(formatFees, numCreatives, currency)
  if (formatFee) fees.push(formatFee)

  return fees
}

/**
 * The gross → fees → net CPM breakdown for a line item.
 *
 * Pass the gross CPM from the form rather than from the saved line item, so the
 * net CPM tracks what the user is typing. The creative format fees come from the
 * line item's creatives — read from the same query the creatives panel fills,
 * so opening the page costs one fetch either way — and only creatives that can
 * actually serve are counted, since a blocked one never charges its fee.
 */
export function useCpmBreakdown(
  lineItemId: string | undefined,
  bid: BidSpecification | undefined,
  grossCpm: CurrencyAmount | undefined
): CpmBreakdown {
  const { data: creatives } = useQuery({
    queryKey: ['lineitem-creatives', lineItemId],
    queryFn: () => CreativesApi.getByLineItem(lineItemId!),
    enabled: !!lineItemId,
  })

  return useMemo(() => {
    const currency = grossCpm?.currency
    const servable = (creatives ?? []).filter((c) => !c.blocksAdServing)
    const formatFees = servable
      .map((c) => c.creativeFormatFee)
      .filter((fee): fee is Fee => !!fee?.type)

    const fees = cpmFeesOf(bid, currency, formatFees, servable.length)
    const netCpm = calculateNetCpm(fees, grossCpm)
    return {
      grossCpm,
      fees,
      netCpm: netCpm && { currency: netCpm.currency, amount: netCpm.amount },
      netCpmMax: netCpm?.maxAmount,
      feesExceedGross: !!netCpm && netCpm.amount < 0,
    }
  }, [bid, grossCpm, creatives])
}
